import React, { useState, useEffect, useContext } from "react";
import { Moon, Sun } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import Logo from "../assets/images/logo/ps-logo.png";
import { IoIosNotifications } from "react-icons/io";
import { roleMapping } from "../utils/roleMapping";
import { NavLink } from "react-router-dom";
import { notification } from "antd";
import NotificationsWindow from "./NotificationsWindow";
import { AppContext } from "../context/AppContextProvider";
import io from "socket.io-client";
import profile from "../assets/images/placeholder-icon.png";
import { useLocation } from "react-router-dom";
import { useQueryClient, useMutation } from "react-query";
import LanguageOption from "../utils/language-dropdown";
import { normalizeLanguageCode } from "../utils/i18nLanguage";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import useAxiosInstance from "../lib/useAxiosInstance";
import ConstituencyPlaybookModal from "./ConstituencyPlaybookModal";
import SirReportModal from "./SirReportModal";
import ElectionModePlaybookModal from "./ElectionMode/ElectionModePlaybookModal";
import ReportsMenu from "./ReportsMenu";
import useReportsAccess from "../hooks/useReportsAccess";

function Navbar2() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const { isOpen, themes, setThemes } = useContext(AppContext);
  const [showNotificationWindow, setShowNotificationWindow] = useState(false);
  const [photoUrl, setPhotoUrl] = useState();
  // user data
  const userString = localStorage.getItem("user");
  const user = JSON.parse(userString);
  const overviewPath = user?.isElectionMode ? "/election-mode" : "/dashboard";

  // FOR UNREAD MESSAGE
  const bgColor = "var(--notification-unread-background)";
  const [seeAllLink, setSeeAllLink] = useState("var(--dark-color)");
  const [unreadNotifications, setUnreadNotifications] = useState();
  const [showNotificationDot, setShowNotificationDot] = useState(false);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL, {
      transports: ["websocket", "polling", "flashsocket"],
    });

    socket.on(user?._id, (data) => {
      notification.open({
        placement: "top",
        message: data.title + " Notification",
        description:
          data?.content.charAt(0).toUpperCase() + data?.content.slice(1),
        duration: 400,
        icon: <IoIosNotifications color="#004877" />,
      });
      setShowNotificationDot(true);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const notificationIcon = document.querySelector(".notification-icon");
      const isNotificationIconClicked =
        notificationIcon && notificationIcon.contains(event.target);

      if (isNotificationIconClicked) {
        setShowNotificationWindow((prevState) => !prevState); // toggle notification window
      } else {
        const notificationWindow = document.querySelector(
          ".notification-window"
        );
        const isNotificationWindowClicked =
          notificationWindow && notificationWindow.contains(event.target);

        if (!isNotificationWindowClicked) {
          setShowNotificationWindow(false);
        }
      }
    };

    window.addEventListener("click", handleOutsideClick);

    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const response = await axiosInstance.get(
          `api/clients/info/getProfileImage/${user?._id}`
        );
        const url = response?.data?.data?.profileImageLink?.publicUrl;
        setPhotoUrl(url);
      } catch (error) {
        console.error("Error fetching profile image:", error);
      }
    };

    fetchProfileImage();
  }, []);

  // USER PROFILE DROPDOWN
  const [showTooltip, setShowTooltip] = useState(false);

  // Toggle tooltip directly when clicking user profile icon
  const toggleTooltipDirectly = () => {
    setShowTooltip((prevShowTooltip) => !prevShowTooltip);
  };

  // Function to close the tooltip
  const closeTooltip = () => {
    setShowTooltip(false);
  };

  const handleOutsideClick = (event) => {
    if (event.target && !event.target.closest(".tooltip-container")) {
      closeTooltip();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await markAllReadMutation.mutateAsync();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const markAllReadMutation = useMutation(
    async () => {
      await axiosInstance.post("/api/notification/markAsReadAll");
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("notifications");
        setSeeAllLink("var(--stat-text)");
        setShowNotificationDot(false);
      },
    }
  );
  // Change Navbar Background Color On Scrolling
  const [navBg, setNavBg] = useState(false);
  const changeNavBg = () => {
    if (window.scrollY >= 10) {
      setNavBg(true);
    } else {
      setNavBg(false);
    }
  };
  window.addEventListener("scroll", changeNavBg);

  const handleClick = (value) => {
    localStorage.setItem("selectedLanguage", value);
    // Normalize legacy codes (e.g. "marathi" → "mr") so the change applies
    // instantly using the same resources loaded on a refresh.
    i18next.changeLanguage(normalizeLanguageCode(value));
  };

  const toggleTheme = () => {
    const newThemes = themes === "darkMode" ? "lightMode" : "darkMode";
    setThemes(newThemes);
    // Save theme preference to local storage
    localStorage.setItem("themes", newThemes);
  };

  // bg change in dashboard

  const location = useLocation();
  const [isDashboard, setIsDashboard] = useState(false);

  useEffect(() => {
    setIsDashboard(location.pathname === "/dashboard");
  }, [location]);

  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [showElectionModePlaybookModal, setShowElectionModePlaybookModal] = useState(false);
  const [showSirReportModal, setShowSirReportModal] = useState(false);
  const { hasPiuPlaybook, hasElectionModePlaybook, sirReport } = useReportsAccess();

  const reportItems = [
    hasPiuPlaybook && {
      key: "piu-playbook",
      label: "PIU Playbook",
      onClick: () => setShowPlaybookModal(true),
    },
    hasElectionModePlaybook && {
      key: "election-mode-playbook",
      label: "Election Mode Playbook",
      onClick: () => setShowElectionModePlaybookModal(true),
    },
    sirReport && {
      key: "sir-report",
      label: "SIR Report",
      onClick: () => setShowSirReportModal(true),
    },
  ].filter(Boolean);

  return (
    <>
      {/* {isLoggedIn && ( */}

      <nav className="glass-shell h-[4.5rem] rounded-[22px] mx-4 my-2 px-3 md:px-6 py-3 flex items-center justify-between shadow-lg nav">
        {/* Left side - Logo */}
        <NavLink to={overviewPath} className="logo hidden sm:flex items-center">
          <img src={Logo} alt="Logo" />
        </NavLink>

        {/* Right side - Controls with rounded sidebar container styling */}
        {/* <div className="topbar-controls rounded-[2rem] px-3 md:px-4 py-2 flex items-center gap-2 md:gap-3 shadow-md min-h-[3rem] max-w-full flex-wrap md:flex-nowrap justify-end"> */}
          <div style={{ background: themes === "darkMode" ? "rgba(15,25,45,0.95)" : "rgba(100,116,139,0.8)", backdropFilter: "blur(8px)" }} className="rounded-[2rem] px-4 py-2 flex items-center space-x-3 shadow-md h-[3rem]">
          {reportItems.length > 0 && (
            <>
              <ReportsMenu
                items={reportItems}
                className="bg-gradient-to-r from-orange-500 to-amber-400 text-white text-[11px] md:text-xs font-semibold px-3 md:px-4 py-2 rounded-full shadow-md border border-orange-200/40 whitespace-nowrap"
              />
              <div className="w-px h-5 bg-slate-400/50"></div>
            </>
          )}

          {/* Theme toggle button */}

          <div
            className="relative w-12 h-12 cursor-pointer"
            onClick={toggleTheme}
          >
            <AnimatePresence mode="wait" initial={false}>
              {themes === "darkMode" ? (
                <motion.div
                  key="moon"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex items-center justify-center text-white"
                >
                  <Moon size={26} />
                </motion.div>
              ) : (
                <motion.div
                  key="sun"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex items-center justify-center text-yellow-400"
                >
                  <Sun size={26} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Divider */}
          <div className="w-px h-5 bg-slate-400/50"></div>

          {/* Language selector */}
          <div className="nav-right-item">
            <LanguageOption onChange={handleClick} />
          </div>

          {/* Divider */}
          <div className="w-px h-5 bg-slate-400/50"></div>

          {/* Notifications */}
          <div className="nav-right-item notification-icon">
            {/* <img src={bellIcon} alt="bell icon"/> */}
            <IoIosNotifications style={{ color: "white" }} size="1.3em" />
            <div
              className="notification-dot"
              style={{
                display: showNotificationDot ? "block" : "none",
                marginRight: "20px",
              }}
            />
          </div>

          {unreadNotifications > 0 && (
            <div className="notification-count">{unreadNotifications}</div>
          )}

          {/* Divider */}
          <div className="w-px h-5 bg-slate-400/50"></div>

          {/* Profile dropdown */}
          <div className="nav-right-item user-profile">
            <div className="nav-right-item user-profile">
              <div className="tooltip-container mr-2">
                <img
                  src={photoUrl || profile}
                  alt="User"
                  style={{ height: "30px", width: "30px" }}
                  onClick={toggleTooltipDirectly}
                />
                {/* {showTooltip && (
                  <div className="tooltip1 backdrop-blur-3xl bg-gradient-to-br from-blue-50 via-yellow-100/10 to-emerald-50">
                    <div className="flex">
                      <div>
                        <img src={photoUrl || profile} alt="user profile" />
                      </div>
                      <div className="user-name">
                        <span className="username">
                          {user?.firstName + " " + user?.lastName}
                        </span>
                        <span>{roleMapping[user?.role] || user?.role}</span>
                        <span>
                          <NavLink
                            to="/settings/profile"
                            className="flex"
                            onClick={closeTooltip}
                          >
                            <span className="mb-2"> {t("View Profile")}</span>{" "}
                            <AiOutlineArrowRight className="ml-1" />
                          </NavLink>
                        </span>
                      </div>
                    </div>
                  </div>
                )} */}
               {showTooltip && (
  <div className="tooltip1 google-tooltip shadow-xl bg-[#e1eef2] rounded-xl w-72 p-3">
    
    {/* Manage Account Button */}
    <NavLink
      to="/settings/profile"
      onClick={closeTooltip}
      className="block w-full text-center text-sm font-medium text-blue-600 border border-[#5ac7e8] rounded-full py-2 hover:bg-gray-50"
    >
      {t("Manage your Account")}
    </NavLink>

    {/* Divider */}
    <div className="my-3 border-t border-[#5ac7e8]"></div>
    {/* Account Info */}
    <div className="flex items-center space-x-3 px-2">
      <img
        src={photoUrl || profile}
        alt="user profile"
        className="w-10 h-10 rounded-full"
      />
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-gray-800">
          {user?.firstName + " " + user?.lastName}
        </span>
        <span className="text-xs text-gray-500">
          {roleMapping[user?.role] || user?.role}
        </span>
      </div>
    </div>

    {/* Divider */}
    <div className="my-3"></div>

    {/* Actions */}
    <div className="flex flex-col text-sm justify-center">
      <div className="border-t border border-gray-900"></div>
      <NavLink
        to="/customersupport"
        onClick={closeTooltip}
        className="px-3 py-2 rounded-lg hover:bg-gray-100 text-center"
      >
        {t("Customer Support")}
      </NavLink>

      <div className="my-1 border-t border border-gray-900"></div>

      <NavLink
        to="/settings"
        onClick={closeTooltip}
        className="px-3 py-2 rounded-lg hover:bg-gray-100 text-center"
      >
        {t("Settings")}
      </NavLink>

      <div className="my-1 border-t border border-gray-900"></div>

      <NavLink
        to="/helpcenter"
        onClick={closeTooltip}
        className="px-3 py-2 rounded-lg hover:bg-gray-100 text-center"
      >
        {t("Help Center")}
      </NavLink>
    </div>
  </div>
)}

              </div>
            </div>
          </div>
        </div>
      </nav>
      {/* )} */}

      {showNotificationWindow && (
        <NotificationsWindow
          setShowNotificationDot={setShowNotificationDot}
          setShowNotificationWindow={setShowNotificationWindow}
          seeAllLink={seeAllLink}
          bgColor={bgColor}
          handleMarkAllRead={handleMarkAllRead}
        />
      )}

      <ConstituencyPlaybookModal
        open={showPlaybookModal}
        onClose={() => setShowPlaybookModal(false)}
      />

      <ElectionModePlaybookModal
        open={showElectionModePlaybookModal}
        onClose={() => setShowElectionModePlaybookModal(false)}
        electionModeType="assembly"
      />

      <SirReportModal
        open={showSirReportModal}
        onClose={() => setShowSirReportModal(false)}
        sirReportId={sirReport?._id}
      />
    </>
  );
}

export default Navbar2;
